import type { AuthProviderPort, StorageUploaderPort } from "./ports";
import { isFirebaseConfigured } from "./firebase/client-config";

export type DeploymentRegion = "international" | "china";

/**
 * Chooses the frontend adapters for this build.
 *
 * The region comes from build configuration, never from the user's
 * language. Someone reading the site in Chinese on the international
 * deployment stays on the international backend; that separation is the
 * whole point of keeping these two settings apart.
 */
export const getDeploymentRegion = (): DeploymentRegion =>
  import.meta.env.VITE_DEPLOYMENT_REGION === "china"
    ? "china"
    : "international";

/**
 * Whether this deployment has a usable provider at all.
 *
 * Answered without loading any SDK, so the "not configured" state can be
 * rendered on a page that never touches the network. The China region has
 * no implementation yet and reports false — it is never dressed up as a
 * working integration.
 */
export const isAuthConfigured = (): boolean =>
  getDeploymentRegion() === "china" ? false : isFirebaseConfigured();

export const isStorageConfigured = (): boolean => isAuthConfigured();

let authProvider: AuthProviderPort | null = null;
let storageUploader: StorageUploaderPort | null = null;
let authLoading: Promise<AuthProviderPort> | null = null;
let storageLoading: Promise<StorageUploaderPort> | null = null;

/**
 * Loads the authentication adapter on demand.
 *
 * Dynamic rather than static because the Firebase Auth SDK is around
 * 200 kB, and a visitor reading the public pages should not download it.
 * It arrives when the session is first checked or a sign-in form is
 * submitted, which keeps it off the critical path for the home page.
 */
export const loadAuthProvider = async (): Promise<AuthProviderPort> => {
  if (authProvider) return authProvider;
  if (!authLoading) {
    authLoading = (async () => {
      const provider =
        getDeploymentRegion() === "china"
          ? new (await import("./china/placeholders")).ChinaAuthPlaceholder()
          : new (
              await import("./firebase/auth-provider")
            ).FirebaseAuthProvider();
      authProvider = provider;
      return provider;
    })();
  }
  return authLoading;
};

export const loadStorageUploader = async (): Promise<StorageUploaderPort> => {
  if (storageUploader) return storageUploader;
  if (!storageLoading) {
    storageLoading = (async () => {
      const uploader =
        getDeploymentRegion() === "china"
          ? new (
              await import("./china/placeholders")
            ).ChinaStorageUploaderPlaceholder()
          : new (
              await import("./firebase/storage-provider")
            ).FirebaseStorageUploader();
      storageUploader = uploader;
      return uploader;
    })();
  }
  return storageLoading;
};

/** Base URL of the business API. Relative by default, matching the Hosting rewrite. */
export const getApiBaseUrl = (): string => {
  const configured = import.meta.env.VITE_API_BASE_URL;
  return typeof configured === "string" && configured.length > 0
    ? configured
    : "/api";
};

/** Replaces the cached adapters. Used by tests. */
export const __setAdaptersForTesting = (
  auth: AuthProviderPort | null,
  storage: StorageUploaderPort | null,
): void => {
  authProvider = auth;
  storageUploader = storage;
  authLoading = auth ? Promise.resolve(auth) : null;
  storageLoading = storage ? Promise.resolve(storage) : null;
};
